import useSWR from "swr";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import { useDocumentData } from "./federation-document";
import type {
  MovementCapabilities,
  MovementDirectory,
} from "src/types/federation-transfer";
export const movementBase = (federation: number) =>
  `/providers/${federation}/federation`;
export function useMovementData<T>(
  federation: number,
  path: string,
  enabled = true,
  workspace?: number,
  params: Record<string, unknown> = {},
) {
  return useDocumentData<T>(federation, path, enabled, {
    ...params,
    workspace_provider_id: workspace,
  });
}
export function useMovementNavigation(enabled = true) {
  const { user } = useAuthContext();
  return useSWR<MovementDirectory>(
    enabled && user?.id
      ? `/providers/federation-transfers/player-navigation#actor-${user.id}`
      : null,
    fetcher,
    { shouldRetryOnError: false, keepPreviousData: false },
  );
}
export function useMovementCapabilities(
  federation: number,
  enabled = true,
  workspace?: number,
) {
  return useMovementData<MovementCapabilities>(
    federation,
    "movements/capabilities",
    enabled,
    workspace,
  );
}
export function useMovementMemberships(workspace: number, enabled = true) {
  return useMovementData<{ items: { provider_id: number; name: string }[] }>(
    workspace,
    "movement-memberships",
    enabled,
  );
}
export async function movementCommand<T>(
  federation: number,
  path: string,
  payload: Record<string, unknown>,
  workspace?: number,
) {
  return (
    await axios.post<T>(`${movementBase(federation)}/${path}`, payload, {
      params: { workspace_provider_id: workspace },
    })
  ).data;
}
export async function movementSummary(
  federation: number,
  id: number,
  workspace?: number,
  locale = "en",
) {
  return (
    await axios.get<Blob>(
      `${movementBase(federation)}/movements/${id}/summary`,
      {
        params: { workspace_provider_id: workspace, locale },
        responseType: "blob",
      },
    )
  ).data;
}
export function movementError(error: unknown) {
  const failure = error as {
    detail?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  const detail = failure?.response?.data?.detail ?? failure?.detail;
  if (detail === "federation_debt_action_blocked") return "debt_action_blocked";
  const status = failure?.response?.status;
  if (typeof detail === "string") {
    if (/setup|capability|policy_required|tariff_required/.test(detail))
      return "ft_setup";
    if (/birth_date_required/.test(detail)) return "ft_birth_required";
    if (/objection_proof/.test(detail)) return "ft_proof_required";
    if (/payment_required/.test(detail)) return "ft_payment_required";
    if (/withdrawal_unavailable/.test(detail)) return "ft_withdrawal_blocked";
    if (/conflict|request_pending|overlap/.test(detail)) return "ft_conflict";
    if (/permission|not_found|self_review|verified/.test(detail))
      return "ft_denied";
    if (/closed|terminal|unavailable|invalid|required|mismatch/.test(detail))
      return "ft_invalid";
  }
  if (Array.isArray(detail) || status === 422) return "ft_invalid";
  if ([401, 403, 404].includes(status || 0)) return "ft_denied";
  if (status === 409) return "ft_conflict";
  if (status === 503) return "ft_setup";
  return "ft_error";
}
